/**
 * "Administrator account" block of the Basics tab: user name and how the admin
 * credential is provided.
 *
 *  - default password : the server uses its own configured secret — nothing is entered or shown
 *  - custom password  : entered here (+ confirm), kept in component state only, sent once as the
 *                       write-only `admin_password`, shown as "Custom password (hidden)" in the review
 *  - Key Vault options: the vault-based credentials are only offered when the GitHub workflow runs the deployment (it cannot
 *                       take a password). A password credential can ALSO be stored in an existing Key Vault as a secret.
 */
import clsx from 'clsx'
import formStyles from '@/pages/NewDeploymentPage.module.css'
import { RadioField, TextField } from './FormFields'
import type { AppriseKeyVaultMode } from '@/types'
import { credentialChoices, credentialPickerVisible, passwordStrength, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, storedSecretName } from './appriseCredentials'
import { readServerDefaults } from './appriseDefaults'
import { Banner } from './AppriseFields'
import styles from './AppriseCreateVm.module.css'
import type { AppriseVmFormApi } from './useAppriseVmForm'

const STRENGTH_CLASS = { none: '', weak: styles.strengthWeak, fair: styles.strengthFair, strong: styles.strengthStrong }

function CredentialRadios({ form }: { form: AppriseVmFormApi }) {
  const choices = credentialChoices(form.options)
  const mode = form.config.security.credential_mode
  return (
    <div className={clsx(formStyles.formGroup, styles.fullRow)} role="radiogroup" aria-label="Authentication">
      <label className={formStyles.label}>Authentication <span className={formStyles.required}>*</span></label>
      <div className={formStyles.radioGroup} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        {choices.map((c) => (
          <div key={c.value}>
            <label className={clsx(formStyles.radioOption, mode === c.value && formStyles.radioOptionSelected)}
              style={c.disabledReason ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}>
              <input type="radio" name="ap_credential" checked={mode === c.value} disabled={!!c.disabledReason}
                onChange={() => form.chooseCredential(c.value)} />
              {c.label}
            </label>
            {c.disabledReason && <span className={formStyles.fieldHint} style={{ display: 'block' }}>{c.disabledReason}</span>}
          </div>
        ))}
      </div>
      {form.validation.errors['security.credential_mode'] && <span className={formStyles.errorMsg}>{form.validation.errors['security.credential_mode']}</span>}
    </div>
  )
}

function CustomPassword({ form }: { form: AppriseVmFormApi }) {
  const err = form.validation.errors
  const strength = passwordStrength(form.password.value)
  return (
    <>
      <div className={formStyles.formGroup}>
        <label className={formStyles.label}>Password <span className={formStyles.required}>*</span></label>
        <input type="password" aria-label="Password" autoComplete="new-password" spellCheck={false}
          className={clsx(formStyles.input, err['security.admin_password'] && formStyles.inputError)}
          value={form.password.value} onChange={(e) => form.setPassword(e.target.value)} />
        {err['security.admin_password']
          ? <span className={formStyles.errorMsg}>{err['security.admin_password']}</span>
          : <div className={formStyles.fieldHint}>
              {PASSWORD_MIN_LENGTH}–{PASSWORD_MAX_LENGTH} characters, at least 3 of: lowercase, uppercase, digit, special character; must not contain the user name.
            </div>}
        {strength.level !== 'none' && (
          <span className={clsx(styles.muted, STRENGTH_CLASS[strength.level])} aria-live="polite">Strength: {strength.label}</span>
        )}
      </div>
      <div className={formStyles.formGroup}>
        <label className={formStyles.label}>Confirm password <span className={formStyles.required}>*</span></label>
        <input type="password" aria-label="Confirm password" autoComplete="new-password" spellCheck={false}
          className={clsx(formStyles.input, err['security.admin_password_confirm'] && formStyles.inputError)}
          value={form.password.confirm} onChange={(e) => form.setPasswordConfirm(e.target.value)} />
        {err['security.admin_password_confirm'] && <span className={formStyles.errorMsg}>{err['security.admin_password_confirm']}</span>}
      </div>
      <Banner>
        The password is kept in this page only and sent once, encrypted at rest on the server. It is never shown again — not in the
        review, the plan or the deployment details.
      </Banner>
    </>
  )
}

function KeyVaultCredential({ form }: { form: AppriseVmFormApi }) {
  const sec = form.config.security
  const err = (k: string) => form.validation.errors[k]
  // A vault the server presets is used as it is: the requester is not asked whether to create another one.
  const presetLocked = readServerDefaults(form.options.defaults).key_vault !== null && sec.key_vault_mode === 'existing'
  return (
    <>
      {!presetLocked && (
        <RadioField<AppriseKeyVaultMode> name="ap_kv_mode" label="Key Vault" value={sec.key_vault_mode} error={err('security.key_vault_mode')}
          options={[{ value: 'create_new', label: 'Create New' }, { value: 'existing', label: 'Existing' }]}
          onChange={(v) => form.setSecurity({ key_vault_mode: v })} />
      )}
      <TextField label="Key Vault Name" required mono value={sec.key_vault_name} error={err('security.key_vault_name')}
        hint="3–24 characters, globally unique" onChange={(v) => form.setSecurity({ key_vault_name: v })} />
      {sec.key_vault_mode === 'existing' && (
        <TextField label="Key Vault Resource Group" required mono value={sec.key_vault_resource_group}
          error={err('security.key_vault_resource_group')} onChange={(v) => form.setSecurity({ key_vault_resource_group: v })} />
      )}
      {sec.credential_mode === 'existing_key_vault_secret' && (
        <TextField label="Key Vault Secret Name" required mono value={sec.credential_secret_name}
          error={err('security.credential_secret_name')} hint="Only the name of the secret — never its value"
          onChange={(v) => form.setSecurity({ credential_secret_name: v })} />
      )}
    </>
  )
}

/** Password credentials: optionally keep the password in an existing Key Vault, as the secret <vm>-admin-password. */
function PasswordKeyVault({ form }: { form: AppriseVmFormApi }) {
  const sec = form.config.security
  const err = (k: string) => form.validation.errors[k]
  const stored = sec.key_vault_mode === 'existing'
  return (
    <>
      <RadioField<AppriseKeyVaultMode> name="ap_kv_store" label="Store the password in Azure Key Vault" value={sec.key_vault_mode}
        error={err('security.key_vault_mode')}
        options={[{ value: 'existing', label: 'Yes, in an existing Key Vault' }, { value: 'none', label: 'No' }]}
        onChange={(v) => form.setSecurity({ key_vault_mode: v })} />
      {stored && (
        <>
          <TextField label="Key Vault Name" required mono value={sec.key_vault_name} error={err('security.key_vault_name')}
            hint="3–24 characters" onChange={(v) => form.setSecurity({ key_vault_name: v })} />
          <TextField label="Key Vault Resource Group" required mono value={sec.key_vault_resource_group}
            error={err('security.key_vault_resource_group')} onChange={(v) => form.setSecurity({ key_vault_resource_group: v })} />
          <Banner>
            The password is stored in the vault as the secret <b>{storedSecretName(sec, form.config.server.server_name, form.config.server.os_type)}</b>
            (tagged with the user name). Its value is never shown in the portal, the plan or the deployment details.
          </Banner>
        </>
      )}
    </>
  )
}

export function AppriseAdminAccount({ form }: { form: AppriseVmFormApi }) {
  const sec = form.config.security
  const err = form.validation.errors
  return (
    <>
      <div className={styles.sectionTitle}>Administrator account</div>
      <TextField label="Username" required mono value={form.config.server.admin_username} error={err['server.admin_username']}
        hint="Local administrator account created on the VM (reserved names such as administrator / root are rejected)"
        onChange={(v) => form.setServer({ admin_username: v })} />
      {credentialPickerVisible(form.options) && <CredentialRadios form={form} />}
      {sec.credential_mode === 'default_password' && (
        <Banner>
          The administrator password is configured on the server by your administrator. It is used to create the VM and is never
          shown in the portal.
        </Banner>
      )}
      {sec.credential_mode === 'custom_password' && <CustomPassword form={form} />}
      {(sec.credential_mode === 'default_password' || sec.credential_mode === 'custom_password') && <PasswordKeyVault form={form} />}
      {(sec.credential_mode === 'generate_in_key_vault' || sec.credential_mode === 'existing_key_vault_secret') && (
        <>
          <Banner>
            {sec.credential_mode === 'generate_in_key_vault'
              ? <>A strong password is generated when the server is created and stored in Azure Key Vault as the secret{' '}
                  <b>{storedSecretName(sec, form.config.server.server_name, form.config.server.os_type)}</b> (tagged with the user name).</>
              : <>No password is entered here: the credential is read from an existing Key Vault secret.</>}
            {' '}The value is never shown in the portal, the plan, logs or outputs.
          </Banner>
          <KeyVaultCredential form={form} />
        </>
      )}
    </>
  )
}
